import { Component, EventEmitter, Input, Output, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CameraDto, DetectionPointDto, PointDirection, UpdateDetectionPointRequest } from '../../../../../api/gen';

/** What the card emits to save an edit of the point (lane schedule included). */
export interface DetectionPointChange {
  detectionPointId: string;
  request: UpdateDetectionPointRequest;
}

const DEFAULT_LANE_HANDOVER_MINUTES = 5;
const MIN_LANE_HANDOVER_MINUTES = 2;

/** API TimeOnly "HH:mm:ss" → `<input type="time">` "HH:mm". */
function toInputTime(value: string | null | undefined, fallback: string): string {
  return value ? value.slice(0, 5) : fallback;
}

const DAY_MINUTES = 1440;

/** One coloured stretch of the 24-hour bar, in percent of the day. */
export interface LaneSegment {
  kind: 'entry' | 'exit' | 'closed';
  left: number;
  width: number;
}

/** A lane schedule as the operator reads it: each side's open window and the 24-hour bar. */
export interface LaneWindows {
  entry: string;
  exit: string;
  handover: number;
  segments: LaneSegment[];
}

function toMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

/**
 * Formats in the browser's locale, the same clock `<input type="time">` uses — so the overview reads
 * "06:00 AM" next to a 12-hour picker and "06:00" next to a 24-hour one.
 */
const TIME_FORMAT = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' });

function formatMinutes(minutes: number): string {
  const total = ((minutes % DAY_MINUTES) + DAY_MINUTES) % DAY_MINUTES;
  // A fixed winter date: no DST jump can shift the wall-clock time.
  return TIME_FORMAT.format(new Date(2000, 0, 1, Math.floor(total / 60), total % 60));
}

/** Labels under the 24-hour bar: midnight, 6, noon, 18, midnight. */
export const LANE_BAR_TICKS = [0, 6, 12, 18, 24].map(h => formatMinutes(h * 60));

/** A stretch of the day starting at `start`, split in two where it crosses midnight. */
function span(kind: LaneSegment['kind'], start: number, length: number): LaneSegment[] {
  if (length <= 0) return [];
  const from = ((start % DAY_MINUTES) + DAY_MINUTES) % DAY_MINUTES;
  const first = Math.min(length, DAY_MINUTES - from);
  const parts = [{ kind, left: (from / DAY_MINUTES) * 100, width: (first / DAY_MINUTES) * 100 }];
  if (length > first) {
    parts.push({ kind, left: 0, width: ((length - first) / DAY_MINUTES) * 100 });
  }
  return parts;
}

/**
 * Mirrors DetectionPoint.LaneModeAt on the server: entry is open [entryFrom, exitFrom − handover), exit
 * [exitFrom, entryFrom − handover), both closed otherwise. Null when the server would reject the schedule.
 */
function describeLane(entryFrom: string, exitFrom: string, handover: number): LaneWindows | null {
  if (!entryFrom || !exitFrom || entryFrom === exitFrom || !Number.isFinite(handover) || handover < 0) return null;
  const entry = toMinutes(entryFrom);
  const exit = toMinutes(exitFrom);
  const entryLength = (exit - entry + DAY_MINUTES) % DAY_MINUTES;
  const exitLength = (entry - exit + DAY_MINUTES) % DAY_MINUTES;
  if (handover >= entryLength || handover >= exitLength) return null;

  return {
    entry: `${formatMinutes(entry)} – ${formatMinutes(exit - handover)}`,
    exit: `${formatMinutes(exit)} – ${formatMinutes(entry - handover)}`,
    handover,
    // Closed last so its thin mark is drawn on top of the open stretches beside it.
    segments: [
      ...span('entry', entry, entryLength - handover),
      ...span('exit', exit, exitLength - handover),
      ...span('closed', exit - handover, handover),
      ...span('closed', entry - handover, handover)
    ]
  };
}

@Component({
  selector: 'app-zone-detection-point',
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatIconModule],
  templateUrl: './zone-detection-point.component.html',
  styleUrl: './zone-detection-point.component.scss'
})
export class ZoneDetectionPointComponent {
  @Input() detectionPoint!: DetectionPointDto;
  @Input() cameras: CameraDto[] | null | undefined = [];
  @Input() saving = false;
  @Output() delete = new EventEmitter<string>();
  @Output() navigateToCamera = new EventEmitter<string>();
  @Output() save = new EventEmitter<DetectionPointChange>();

  readonly directions = [PointDirection.Approach, PointDirection.Departure, PointDirection.Both, PointDirection.Unknown];
  readonly minHandover = MIN_LANE_HANDOVER_MINUTES;
  readonly barTicks = LANE_BAR_TICKS;

  editing = signal(false);
  name = signal('');
  cameraId = signal('');
  expectedDirections = signal<PointDirection>(PointDirection.Approach);
  description = signal('');
  isActive = signal(true);
  isReversible = signal(false);
  laneEntryFrom = signal('06:00');
  laneExitFrom = signal('12:00');
  laneHandoverMinutes = signal<number>(DEFAULT_LANE_HANDOVER_MINUTES);

  /** A lane schedule needs a point that knows which way cars move (one camera for both, or one per side). */
  formCanBeReversible = computed(() => this.expectedDirections() !== PointDirection.Unknown);

  /** The schedule being edited, as it will run; null while it is not a valid schedule. */
  laneWindowsPreview = computed(() =>
    describeLane(this.laneEntryFrom(), this.laneExitFrom(), this.laneHandoverMinutes()));

  formValid = computed(() => {
    if (this.name().trim().length < 2 || !this.cameraId()) return false;
    if (!this.isReversible() || !this.formCanBeReversible()) return true;
    return this.laneHandoverMinutes() >= MIN_LANE_HANDOVER_MINUTES && this.laneWindowsPreview() !== null;
  });

  /** The group's cameras, plus the point's own camera when it sits in another group of the zone. */
  get cameraOptions(): { id: string; name: string }[] {
    const options = (this.cameras ?? [])
      .filter(c => !!c.id)
      .map(c => ({ id: c.id!, name: c.deviceName ?? c.id! }));
    const own = this.detectionPoint?.camera;
    if (own?.id && !options.some(o => o.id === own.id)) {
      options.unshift({ id: own.id, name: own.deviceName ?? own.id });
    }
    return options;
  }

  get hasLaneSchedule(): boolean {
    return !!(this.detectionPoint?.laneEntryFrom && this.detectionPoint?.laneExitFrom
      && this.detectionPoint?.laneHandoverMinutes != null);
  }

  /** The saved schedule, as it runs. */
  get laneWindows(): LaneWindows | null {
    if (!this.hasLaneSchedule) return null;
    const p = this.detectionPoint;
    return describeLane(toInputTime(p.laneEntryFrom, ''), toInputTime(p.laneExitFrom, ''), p.laneHandoverMinutes!);
  }

  onDelete(): void {
    if (this.detectionPoint.id) {
      this.delete.emit(this.detectionPoint.id);
    }
  }

  onNavigateToCamera(): void {
    if (this.detectionPoint.camera?.id) {
      this.navigateToCamera.emit(this.detectionPoint.camera.id);
    }
  }

  startEdit(): void {
    const p = this.detectionPoint;
    this.name.set(p.name ?? '');
    this.cameraId.set(p.cameraId ?? p.camera?.id ?? '');
    this.expectedDirections.set(p.expectedDirections ?? PointDirection.Approach);
    this.description.set(p.description ?? '');
    this.isActive.set(p.isActive ?? true);
    this.isReversible.set(this.hasLaneSchedule);
    this.laneEntryFrom.set(toInputTime(p.laneEntryFrom, '06:00'));
    this.laneExitFrom.set(toInputTime(p.laneExitFrom, '12:00'));
    this.laneHandoverMinutes.set(p.laneHandoverMinutes ?? DEFAULT_LANE_HANDOVER_MINUTES);
    this.editing.set(true);
  }

  cancelEdit(): void {
    this.editing.set(false);
  }

  onSave(): void {
    if (!this.detectionPoint.id || !this.formValid()) return;

    const reversible = this.isReversible() && this.formCanBeReversible();
    const request: UpdateDetectionPointRequest = {
      name: this.name().trim(),
      cameraId: this.cameraId(),
      expectedDirections: this.expectedDirections(),
      description: this.description().trim(),
      isActive: this.isActive(),
      ...(reversible
        ? {
          laneEntryFrom: `${this.laneEntryFrom()}:00`,
          laneExitFrom: `${this.laneExitFrom()}:00`,
          laneHandoverMinutes: this.laneHandoverMinutes()
        }
        // Turning it off clears the schedule (the server clears the lane's other point too).
        : { clearLaneSchedule: this.hasLaneSchedule })
    };

    this.save.emit({ detectionPointId: this.detectionPoint.id, request });
    this.editing.set(false);
  }

  getDirectionLabel(direction: PointDirection | undefined): string {
    if (!direction) return 'Unknown';
    const directionMap: Record<string, string> = {
      'Approach': '📥 Approach',
      'Departure': '📤 Departure',
      'Both': '↔️ Both',
      'Unknown': '❓ Unknown'
    };
    return directionMap[direction] || direction;
  }
}
